// InvoiceViewModal.jsx
import { useMemo, useState } from "react";
import { X, Download, FileText, Loader2 } from "lucide-react";

function safeDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "" : dt.toISOString();
}

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function num(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
}

export default function InvoiceViewModal({
  invoice,
  onClose,
  onEdit,
  onDownloadPdf,
}) {
  const [downloading, setDownloading] = useState(false);
  const [showPdfOptions, setShowPdfOptions] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(false);

  const inv = invoice || {};

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(Number(amount) || 0);

  const formatDate = (dateString) => {
    const d = safeDate(dateString);
    if (!d) return "-";
    return new Date(d).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const invoiceNo = inv.invoiceNo || inv.invoiceNumber || "-";
  const status = String(inv.status || "draft").toLowerCase();
  const isDraft = status === "draft";

  const from = inv.from || {};
  const billTo = inv.billTo || {};
  const payment = inv.payment || {};
  const totals = inv.totals || {};
  const amounts = inv.amounts || {};
  const rates = inv.rates || {};

  const items =
    Array.isArray(inv.items) && inv.items.length
      ? inv.items
      : Array.isArray(inv.lines)
      ? inv.lines.map((l) => ({
          name: l.productName ?? l.name ?? "",
          description: l.description ?? "",
          qty: Number(l.qty) || 0,
          price: Number(l.unitPrice) || 0,
          amount: Number(l.lineTotal) || 0,
        }))
      : [];

  // ---- numbers for ringkasan (with discount breakdown) ----
  // prefer backend-calculated amounts.* if present; fallback to computed
  const computedGrossSubtotal = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);

  const subtotalGross =
    Number.isFinite(Number(amounts.subtotal_gross)) && num(amounts.subtotal_gross) > 0
      ? num(amounts.subtotal_gross)
      : computedGrossSubtotal;

  const discountAmount =
    Number.isFinite(Number(amounts.discount)) ? num(amounts.discount) : 0;

  // totals.subtotal in your data is already NET (after discount)
  const subtotalNet =
    Number.isFinite(Number(totals.subtotal))
      ? num(totals.subtotal)
      : Math.max(0, subtotalGross - discountAmount);

  const ppn =
    Number.isFinite(Number(totals.ppn))
      ? num(totals.ppn)
      : Number.isFinite(Number(amounts.ppn))
      ? num(amounts.ppn)
      : 0;

  const pph =
    Number.isFinite(Number(totals.pph))
      ? num(totals.pph)
      : Number.isFinite(Number(amounts.pph))
      ? num(amounts.pph)
      : 0;

  const grandTotal =
    Number.isFinite(Number(totals.grandTotal))
      ? num(totals.grandTotal)
      : Math.max(0, subtotalNet + ppn - pph);

  const discountPct =
    Number.isFinite(Number(rates.discount)) ? num(rates.discount) : 0;
  const ppnPct =
    Number.isFinite(Number(rates.ppn)) ? num(rates.ppn) : 0;
  const pphPct =
    Number.isFinite(Number(rates.pph)) ? num(rates.pph) : 0;

  const statusConfig = {
    paid: {
      label: "Lunas",
      pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
    },
    issued: {
      label: "Issued",
      pill: "bg-blue-50 text-blue-700 border-blue-200",
      dot: "bg-blue-500",
    },
    sent: {
      label: "Sent",
      pill: "bg-sky-50 text-sky-700 border-sky-200",
      dot: "bg-sky-500",
    },
    draft: {
      label: "Draft",
      pill: "bg-slate-100 text-slate-700 border-slate-300",
      dot: "bg-slate-400",
    },
    void: {
      label: "Void",
      pill: "bg-gray-100 text-gray-700 border-gray-300",
      dot: "bg-gray-500",
    },
    cancelled: {
      label: "Cancelled",
      pill: "bg-gray-100 text-gray-700 border-gray-300",
      dot: "bg-gray-500",
    },
    overdue: {
      label: "Overdue",
      pill: "bg-rose-50 text-rose-700 border-rose-200",
      dot: "bg-rose-500",
    },
    "paid overdue": {
      label: "Paid Overdue",
      pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
    },
    paid_overdue: {
      label: "Paid Overdue",
      pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
    },
  };

  const pillClass =
    statusConfig[status]?.pill || "bg-slate-100 text-slate-700 border-slate-300";
  const dotClass = statusConfig[status]?.dot || "bg-slate-400";
  const statusLabel = statusConfig[status]?.label || status;

  const reasonMeta = useMemo(() => {
    const st = String(status || "").toLowerCase();

    if (st !== "void" && st !== "cancelled") return null;

    const when =
      st === "void"
        ? inv.voidedAt || inv.voidAt || null
        : inv.cancelledAt || inv.canceledAt || inv.cancelAt || null;

    const reason = String(
      inv.voidReason ||
        inv.void_reason ||
        inv.voidNotes ||
        inv.reasonVoid ||
        inv.reason ||
        inv.notes ||
        ""
    ).trim();

    if (!reason) return null;

    const title = st === "void" ? "Alasan Void" : "Alasan Cancel";
    const badge = st === "void" ? "Void" : "Cancelled";

    const sub =
      when && safeDate(when)
        ? st === "void"
          ? `Di-void pada ${formatDate(when)}`
          : `Dibatalkan pada ${formatDate(when)}`
        : st === "void"
        ? "Invoice ini di-void."
        : "Invoice ini dibatalkan.";

    return { title, badge, sub, reason };
  }, [
    status,
    inv.voidedAt,
    inv.voidAt,
    inv.cancelledAt,
    inv.canceledAt,
    inv.cancelAt,
    inv.voidReason,
    inv.void_reason,
    inv.voidNotes,
    inv.reasonVoid,
    inv.reason,
    inv.notes,
  ]);

  const ReasonCard = useMemo(() => {
    if (!reasonMeta) return null;

    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-rose-900">
              {reasonMeta.title}
            </div>
            <div className="text-xs text-rose-700 mt-1">{reasonMeta.sub}</div>
          </div>

          <div className="shrink-0">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white text-rose-700 border border-rose-200">
              {reasonMeta.badge}
            </span>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-white border border-rose-200 p-4">
          <div className="text-xs text-rose-600 mb-1 font-medium">Catatan</div>
          <div className="text-sm text-slate-900 whitespace-pre-wrap break-words">
            {reasonMeta.reason}
          </div>
        </div>
      </div>
    );
  }, [reasonMeta]);

  const openPdfOptions = () => {
    const def = Boolean(inv.includeSignature); // align with your stored field
    setIncludeSignature(def);
    setShowPdfOptions(true);
  };

  const handleDownload = async () => {
    if (downloading) return;

    if (!onDownloadPdf) {
      alert("onDownloadPdf belum di-wire");
      return;
    }

    try {
      setDownloading(true);
      await onDownloadPdf({ includeSignature: !!includeSignature });
      setShowPdfOptions(false);
    } catch (err) {
      alert(err?.message || "Gagal download PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!invoice) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-slate-900">Detail Invoice</h2>
              <span
                className={cx(
                  "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border",
                  pillClass
                )}
              >
                <span className={cx("inline-block w-1.5 h-1.5 rounded-full", dotClass)} />
                {statusLabel}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2">
                <button
                  type="button"
                  onClick={openPdfOptions}
                  disabled={downloading}
                  className={cx(
                    "inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium transition-all",
                    downloading
                      ? "text-slate-400 bg-slate-50 cursor-not-allowed"
                      : "text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                  )}
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {downloading ? "Downloading..." : "PDF"}
                </button>

                {/* ✅ show Edit ONLY when draft */}
                {isDraft && (
                  <button
                    type="button"
                    onClick={onEdit}
                    disabled={downloading}
                    className={cx(
                      "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      downloading
                        ? "bg-blue-400 text-white cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow"
                    )}
                  >
                    <FileText className="w-4 h-4" />
                    Edit
                  </button>
                )}
              </div>

              <div className="flex sm:hidden items-center gap-2">
                <button
                  type="button"
                  onClick={openPdfOptions}
                  className={cx(
                    "p-2 rounded-lg border border-slate-200 transition-all",
                    downloading
                      ? "text-slate-400 bg-slate-50 cursor-not-allowed"
                      : "text-slate-700 hover:bg-slate-50"
                  )}
                  disabled={downloading}
                >
                  {downloading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                </button>

                {/* ✅ show Edit ONLY when draft */}
                {isDraft && (
                  <button
                    type="button"
                    onClick={onEdit}
                    disabled={downloading}
                    className={cx(
                      "p-2 rounded-lg text-white transition-all",
                      downloading
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    )}
                  >
                    <FileText className="w-5 h-5" />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={onClose}
                disabled={downloading}
                className={cx(
                  "p-2 rounded-lg transition-all",
                  downloading
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                )}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* PDF Options Modal */}
        {showPdfOptions && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={downloading ? undefined : () => setShowPdfOptions(false)}
            />
            <div className="relative bg-white w-[92vw] max-w-md rounded-xl shadow-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <div className="text-lg font-bold text-slate-900">Download PDF</div>
                  <div className="text-sm text-slate-500 mt-1">
                    Invoice:{" "}
                    <span className="font-semibold text-slate-700">
                      {invoiceNo}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                  disabled={downloading}
                  onClick={() => setShowPdfOptions(false)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-slate-600 mb-4">
                  Pilih apakah tanda tangan ikut dimasukkan ke PDF.
                </p>

                <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={includeSignature}
                    onChange={(e) => setIncludeSignature(e.target.checked)}
                    disabled={downloading}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Include signature
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowPdfOptions(false)}
                  disabled={downloading}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading}
                  className={cx(
                    "px-5 py-2 rounded-lg text-white font-semibold transition-all",
                    downloading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow"
                  )}
                >
                  {downloading ? "Downloading..." : "Download"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(92vh-73px)] bg-slate-50">
          <div className="p-6 space-y-5">
            {/* Reason Card */}
            {ReasonCard}

            {/* Invoice Header */}
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-6 py-8 text-white">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
                  <div>
                    <div className="text-4xl font-black tracking-tight">INVOICE</div>
                    <div className="mt-3 flex items-baseline gap-2">
                      <span className="text-blue-100 text-sm">No.</span>
                      <span className="font-bold text-white text-base">{invoiceNo}</span>
                    </div>
                  </div>

                  <div className="sm:text-right space-y-3">
                    <div>
                      <div className="text-xs text-blue-100 mb-1">Tanggal Invoice</div>
                      <div className="text-base font-bold">
                        {formatDate(inv.invoiceDate)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-blue-100 mb-1">Jatuh Tempo</div>
                      <div className="text-base font-bold">
                        {formatDate(inv.dueDate)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white px-6 py-4 border-t border-blue-700/10">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-slate-50 rounded-lg px-4 py-3 border border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Status</div>
                    <div className="text-sm font-bold text-slate-900">{statusLabel}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-4 py-3 border border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Subtotal</div>
                    <div className="text-sm font-bold text-slate-900">
                      {formatCurrency(subtotalNet)}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-4 py-3 border border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Total</div>
                    <div className="text-sm font-bold text-slate-900">
                      {formatCurrency(grandTotal)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Parties */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* From */}
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-bold text-slate-900">Dari</h3>
                  <span className="text-xs text-slate-500 font-medium">Pengirim</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1.5 font-medium">Perusahaan</div>
                    <div className="text-sm text-slate-900 font-semibold">
                      {from.companyName || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1.5 font-medium">Email</div>
                    <div className="text-sm text-slate-900">
                      {from.companyEmail || "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1.5 font-medium">Alamat</div>
                    <div className="text-sm text-slate-900 whitespace-pre-wrap leading-relaxed">
                      {from.companyAddress || "-"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bill To */}
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-bold text-slate-900">Ditagihkan Kepada</h3>
                  <span className="text-xs text-slate-500 font-medium">Penerima</span>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1.5 font-medium">Perusahaan</div>
                    <div className="text-sm text-slate-900 font-semibold">
                      {billTo.companyName || "-"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="text-xs text-slate-500 mb-1 font-medium">PIC</div>
                      <div className="text-sm font-semibold text-slate-900">
                        {billTo.picName || "-"}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="text-xs text-slate-500 mb-1 font-medium">Jabatan</div>
                      <div className="text-sm font-semibold text-slate-900">
                        {billTo.picTitle || "-"}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-slate-500 mb-1.5 font-medium">Alamat</div>
                    <div className="text-sm text-slate-900 whitespace-pre-wrap leading-relaxed">
                      {billTo.companyAddress || "-"}
                    </div>
                  </div>

                  {(billTo.emailOrPhone || billTo.reference) && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-xs text-slate-500 mb-1 font-medium">Email / Phone</div>
                      <div className="text-sm font-semibold text-slate-900 break-all">
                    {billTo.emailOrPhone || "-"}
                  </div>

                          </div>
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-xs text-slate-500 mb-1 font-medium">Reference</div>
                        <div className="text-sm font-semibold text-slate-900">
                          {billTo.reference || "-"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Item Invoice</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    * Diskon/Pajak per item (jika ada) akan muncul di deskripsi item.
                  </p>
                </div>
                <div className="text-xs text-slate-600">
                  Total item:{" "}
                  <span className="font-bold text-slate-900">{items.length}</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold tracking-wide text-white w-56">
                        Produk
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold tracking-wide text-white">
                        Deskripsi
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-bold tracking-wide text-white w-24">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-bold tracking-wide text-white w-44">
                        Harga
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-bold tracking-wide text-white w-44">
                        Jumlah
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200">
                    {items.map((it, idx) => (
                      <tr
                        key={`${idx}-${it.description}-${it.name}`}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                          {it.name || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                          {it.description || "Item"}
                        </td>
                        <td className="px-4 py-4 text-sm text-right text-slate-900 font-medium">
                          {Number(it.qty) || 0}
                        </td>
                        <td className="px-4 py-4 text-sm text-right text-slate-900">
                          {formatCurrency(it.price)}
                        </td>
                        <td className="px-6 py-4 text-sm text-right font-bold text-slate-900">
                          {formatCurrency(it.amount)}
                        </td>
                      </tr>
                    ))}

                    {!items.length && (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                          Tidak ada item.
                        </td>
                      </tr>
                    )}
                                   </tbody>

                  <tfoot>
                    <tr className="border-t border-slate-200 bg-white">
                      {/* Produk + Deskripsi + Qty + Harga */}
                      <td colSpan={4} className="px-6 py-4 text-right text-sm font-semibold text-slate-600">
                        Subtotal
                      </td>

                      {/* Jumlah */}
                      <td className="px-6 py-4 text-sm text-right font-bold text-slate-900 whitespace-nowrap">
                        {formatCurrency(subtotalNet)}
                      </td>
                    </tr>
                  </tfoot>
                </table>

              </div>
            </div>

            {/* Payment + Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              {/* Payment Info */}
              <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="text-sm font-bold text-slate-900 mb-5">
                  Informasi Pembayaran
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-6">
                    <div className="text-xs text-slate-500 font-medium">Bank</div>
                    <div className="text-sm text-slate-900 text-right font-semibold">
                      {payment.bank || "-"}
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-6">
                    <div className="text-xs text-slate-500 font-medium">
                      Nama Rekening
                    </div>
                    <div className="text-sm text-slate-900 text-right font-semibold">
                      {payment.accountName || "-"}
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-6">
                    <div className="text-xs text-slate-500 font-medium">
                      No. Rekening
                    </div>
                    <div className="text-sm text-slate-900 text-right font-mono tracking-tight">
                      {payment.accountNo || "-"}
                    </div>
                  </div>
                  {payment.notes && (
                    <div className="pt-2">
                      <div className="text-xs text-slate-500 mb-2 font-medium">
                        Catatan
                      </div>
                      <div className="text-sm text-slate-900 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-200 leading-relaxed">
                        {payment.notes}
                      </div>
                    </div>
                  )}
                </div>

                {(inv.signatureName || inv.signatureTitle) && (
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <h4 className="text-sm font-bold text-slate-900 mb-4">
                      Penandatangan
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-xs text-slate-500 mb-1 font-medium">
                          Nama
                        </div>
                        <div className="text-sm font-semibold text-slate-900">
                          {inv.signatureName || "-"}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-xs text-slate-500 mb-1 font-medium">
                          Jabatan
                        </div>
                        <div className="text-sm font-semibold text-slate-900">
                          {inv.signatureTitle || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary (✅ includes discount breakdown) */}
              <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-sm font-bold text-slate-900">Ringkasan</h3>
                  <span className="text-xs text-slate-500 font-medium">
                    Total pembayaran
                  </span>
                </div>

                <div className="space-y-3">
                               <div className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      Total PPN{ppnPct ? <span className="ml-1 text-xs text-slate-400">({ppnPct.toFixed(2)}%)</span> : null}
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(ppn)}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      Total PPh{pphPct ? <span className="ml-1 text-xs text-slate-400">({pphPct.toFixed(2)}%)</span> : null}
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(pph)}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      Total Discount{discountPct ? <span className="ml-1 text-xs text-slate-400">({discountPct}% disc)</span> : null}
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatCurrency(discountAmount)}
                    </span>
                  </div>


                  <div className="pt-4 mt-4 border-t-2 border-slate-300">
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="text-xs text-slate-500 mb-1 font-medium">
                          Total
                        </div>
                        <div className="text-2xl font-black text-slate-900 leading-tight">
                          {formatCurrency(grandTotal)}
                        </div>
                      </div>
                    
                    </div>
                  </div>

                  {/* Optional: tiny explanation line */}
                  <div className="pt-2">
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Total = Subtotal Net + PPN − PPh
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex items-center justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={downloading}
                className={cx(
                  "px-6 py-2.5 rounded-lg border border-slate-300 font-semibold transition-all",
                  downloading
                    ? "text-slate-400 bg-slate-50 cursor-not-allowed"
                    : "text-slate-700 hover:bg-slate-50 hover:border-slate-400"
                )}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
